/**
 *
 * @module drmClient
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/drm.md
 * @see https://github.bamtech.co/services-commons/mdrm/tree/master/basic_apis
 * @see https://github.bamtech.co/services-commons/mdrm/blob/master/license_acquisition_urls.md
 *
 */

import Check from 'check-types';
import Token from './../token/entry';
import Exception from './../exception/entry';
import HttpClient from './../platforms/httpClient';
import Logging from './../../logging/entry';
import Util from './../util/entry';
import DustLogUtility from './../internal/dust/dustLogUtility';
import DustUrnReference from './../internal/dust/dustUrnReference';
import Configuration from './../configuration/entry';

import DrmClientEndpoint from './drmClientEndpoint';
import DrmClientConfiguration from './drmClientConfiguration';
import PlayReadyMessage from './playReadyMessage';

const InvalidArgumentException = Exception.InvalidArgumentException;
const ServiceException = Exception.ServiceException;

const AccessToken = Token.AccessToken;
const HttpMethod = Configuration.HttpMethod;
const DrmClientDustUrnReference = DustUrnReference.services.drm.drmClient;

const {
    nonEmptyString,
    instanceStrict
} = Check.assert;

export default class DrmClient {

    /**
     *
     * @param {DrmClientConfiguration} drmClientConfiguration
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {IHttpClient} [httpClient=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(drmClientConfiguration, logger = Logging, httpClient = null) {

        const errorMessage = {
            ctor: 'DrmClient(drmClientConfiguration, logger, httpClient)',
            get drmClientConfiguration() {
                return `${this.ctor} needs DrmClientConfiguration instance`;
            }
        };

        instanceStrict(
            drmClientConfiguration,
            DrmClientConfiguration,
            errorMessage.drmClientConfiguration,
            InvalidArgumentException
        );

        /**
         *
         * @access private
         * @type {DrmClientConfiguration}
         * @desc The configuration information to use.
         *
         */
        this.config = drmClientConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {HttpClient}
         * @desc The object responsible for making HTTP requests.
         *
         */
        this.httpClient = httpClient || new HttpClient(logger);

        /**
         *
         * @access private
         * @type {Boolean}
         * @note needs to default to true to collect dust events before the configuration is fetched and we can
         * determine if this should be enabled
         *
         */
        this.dustEnabled = true;

        this.logger.log(DrmClient.name, 'Created.');

    }

    /**
     *
     * @access private
     * @param {String} keyLocation - The URL of the decryption key.
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Retrieves a SILK decryption key from the key service.
     * @returns {Promise<Array<Byte>>} The decryption key for this event.
     *
     */
    getSilkKey(keyLocation, accessToken) {

        const errorMessage = {
            method: 'DrmClient.getSilkKey(keyLocation, accessToken)',
            get keyLocation() {
                return `${this.method} needs keyLocation String`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        nonEmptyString(keyLocation, errorMessage.keyLocation, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            DrmClientEndpoint.silkKey,
            undefined,
            'json'
        );

        // set the payload URL based on the provided key location
        payload.url = keyLocation;

        logger.info(DrmClient.name, `Attempting to retreive SilkKey from keyLocation: ${keyLocation}.`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: DrmClient.name,
            urn: DrmClientDustUrnReference.getSilkKey,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @desc Retrieves a SILK decryption key from the key service.
     * @returns {Promise<ArrayBuffer>} A Promise fulfilled by the
     * BAMTECH FairPlay Application Certificate as binary data.
     * @note This certificate is the same for all partners.
     * It will be hosted statically and can be stored on a long-term basis.
     *
     */
    getFairPlayCertificate(accessToken) {

        const errorMessage = {
            method: 'DrmClient.getFairPlayCertificate(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            DrmClientEndpoint.fairPlayCertificate
        );

        logger.info(DrmClient.name, 'Attempting to retreive FairPlay Certificate.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: DrmClient.name,
            urn: DrmClientDustUrnReference.getFairPlayCertificate,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @param {Array<Byte>} serverPlaybackContext - A server playback context created by the DRM client (typically
     * directly by the player or the platform).
     * @param {SDK.Services.Token.AccessToken} accessToken - The access token to provide user context.
     * @desc Requests a FairPlay content key context from the license service to autohrize and decrypt media.
     * @returns {Promise<Array<Byte>>} A Promise fulfilled by a content key context from the license server.
     * @note This certificate is the same for all partners. It will be hosted statically and can be stored on a
     * long-term basis.
     *
     */
    getFairPlayLicense(serverPlaybackContext, accessToken) {

        const errorMessage = {
            method: 'DrmClient.getFairPlayLicense(serverPlaybackContext, accessToken)',
            get serverPlaybackContextString() {
                return `${this.method} serverPlaybackContext should be a String with length`;
            },
            get serverPlaybackContext() {
                return `${this.method} buffer should be an ArrayBuffer view`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        if (Check.not.string(serverPlaybackContext)) {

            Check.assert(
                ArrayBuffer.isView(serverPlaybackContext),
                errorMessage.serverPlaybackContext,
                InvalidArgumentException
            );

        } else {

            nonEmptyString(serverPlaybackContext, errorMessage.serverPlaybackContext, InvalidArgumentException);

        }

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            DrmClientEndpoint.fairPlayLicense,
            serverPlaybackContext
        );

        logger.info(DrmClient.name, `Attempting to retreive FairPlay License: ${JSON.stringify(serverPlaybackContext)}.`); // eslint-disable-line max-len

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: DrmClient.name,
            urn: DrmClientDustUrnReference.getFairPlayLicense,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @desc Retrieves the BAMTECH Widevine Service Certificate.
     * @returns {Promise<ArrayBuffer>} A Promise fulfilled by the
     * BAMTECH Widevine Service Certificate as binary data.
     * @note This certificate is the same for all partners.
     * It will be hosted statically and can be stored on a long-term basis.
     *
     */
    getWidevineCertificate(accessToken) {

        const errorMessage = {
            method: 'DrmClient.getWidevineCertificate(accessToken)',
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            DrmClientEndpoint.widevineCertificate
        );

        logger.info(DrmClient.name, 'Attempting to retreive Widevine Certificate.');

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: DrmClient.name,
            urn: DrmClientDustUrnReference.getWidevineCertificate,
            payload,
            method: HttpMethod.GET
        });

        return httpClient.get(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @param {Array<Byte>} buffer - A protocol buffer conforming to
     * the Widevine specification identifying the asset and encryption mode.
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @desc Requests a Widevine License from the license service.
     * @returns {Promise<Array<Byte>>} A Promise fulfilled with the Widevine license as binary data.
     * @note Nearly all Widevine DRM clients will provide a mechanism to create the Widevine protocol buffer. The
     * SDK shall leverage these capabilities wherever possible.
     *
     */
    getWidevineLicense(buffer, accessToken) {

        const errorMessage = {
            method: 'DrmClient.getWidevineLicense(buffer, accessToken)',
            get buffer() {
                return `${this.method} buffer should be an ArrayBuffer view`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        Check.assert(ArrayBuffer.isView(buffer), errorMessage.buffer, InvalidArgumentException);

        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            DrmClientEndpoint.widevineLicense,
            buffer
        );

        logger.info(DrmClient.name, `Attempting to retreive Widevine License with: ${JSON.stringify(buffer)}.`);

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: DrmClient.name,
            urn: DrmClientDustUrnReference.getWidevineLicense,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /**
     *
     * @access private
     * @param {PlayReadyMessage} message - A configured `PlayReadyMessage` containing
     * any required headers and a `PlayReadyObject`.
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @desc Reqests a PlayReady license from the license server.
     * @returns {Promise<Array<Byte>>} A Promise fulfilled with the PlayReady license as binary data.
     *
     */
    getPlayReadyLicense(message, accessToken) {

        const errorMessage = {
            method: 'DrmClient.getPlayReadyLicense(message, accessToken)',
            get message() {
                return `${this.method} message needs to be an instance of PlayReadyMessage`;
            },
            get accessToken() {
                return `${this.method} needs SDK.Services.Token.AccessToken instance`;
            }
        };

        instanceStrict(message, PlayReadyMessage, errorMessage.message, InvalidArgumentException);
        instanceStrict(accessToken, AccessToken, errorMessage.accessToken, InvalidArgumentException);

        const { dustEnabled, logger, httpClient } = this;

        const payload = this.getPayload(
            accessToken,
            DrmClientEndpoint.playReadyLicense,
            Util.getSafe(() => message.body.xmlData) ? message.body.xmlData : undefined
        );

        logger.info(DrmClient.name, `Attempting to retreive PlayReady License: message: ${JSON.stringify(message.body)}.`); // eslint-disable-line max-len

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: DrmClient.name,
            urn: DrmClientDustUrnReference.getPlayReadyLicense,
            payload,
            method: HttpMethod.POST
        });

        return httpClient.post(payload).catch((exceptionData) => {

            throw new ServiceException({ exceptionData });

        }).then((response) => {

            return Util.checkResponseCode(response, dustLogUtility);

        }).then((response) => {

            const { data } = response;

            return Promise.resolve(data);

        }).finally(() => {

            return dustEnabled ? dustLogUtility.log() : Promise.resolve();

        });

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {SDK.Services.Token.AccessToken} accessToken
     * @param {DrmClientEndpoint} rel
     * @param {Object} body
     * @param {String} [bodyType='arrayBuffer'] - default `bodyType` for DRM requests
     * @returns {Object} The payload for the client call.
     *
     */
    getPayload(accessToken, rel, body, bodyType = 'arrayBuffer') {

        const { endpoints } = this.config;
        const endpoint = endpoints[rel];
        const { href, headers } = endpoint;
        const requestHeaders = Object.assign({}, headers);
        const url = href;

        requestHeaders.Authorization = requestHeaders.Authorization.replace(/\{accessToken\}/gi, accessToken.token);

        return {
            url,
            body,
            bodyType,
            headers: requestHeaders
        };

    }

}



// WEBPACK FOOTER //
// ./src/services/drm/drmClient.js